import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import Layout from "../components/Layout";
import {
  Users, ArrowLeftRight, AlertTriangle, Shield,
  CheckCircle, Clock, TrendingUp, Eye, FileText,
  Search, BarChart3
} from "lucide-react";
import {
  PieChart, Pie, Cell, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHART_COLORS = ["#0d9488", "#f59e0b", "#ef4444", "#8b5cf6", "#3b82f6", "#ec4899"];
const RISK_COLORS = { Low: "#10b981", Medium: "#f59e0b", High: "#f97316", Critical: "#ef4444" };

function MetricCard({ label, value, icon: Icon, color, subtitle }) {
  const colorMap = {
    teal: "bg-teal-500/10 text-teal-400 border-teal-500/20",
    amber: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    emerald: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    red: "bg-red-500/10 text-red-400 border-red-500/20",
    violet: "bg-violet-500/10 text-violet-400 border-violet-500/20",
    sky: "bg-sky-500/10 text-sky-400 border-sky-500/20",
  };
  return (
    <div className={`border rounded-xl p-4 ${colorMap[color] || colorMap.teal}`} data-testid={`metric-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between mb-2">
        <Icon size={18} />
        <span className="text-2xl font-bold">{typeof value === "number" ? value.toLocaleString() : value}</span>
      </div>
      <p className="text-sm opacity-80">{label}</p>
      {subtitle && <p className="text-xs opacity-50 mt-1">{subtitle}</p>}
    </div>
  );
}

function ChartCard({ title, children, testId }) {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-5" data-testid={testId}>
      <h3 className="text-sm font-semibold text-white mb-4">{title}</h3>
      {children}
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-xs">
      <p className="text-slate-300 font-medium mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full inline-block" style={{ background: p.color }} />
          {p.name}: {typeof p.value === "number" ? p.value.toLocaleString() : p.value}
        </p>
      ))}
    </div>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [statsRes, analyticsRes] = await Promise.all([
          axios.get(`${API}/dashboard/stats`, { withCredentials: true }),
          axios.get(`${API}/dashboard/analytics`, { withCredentials: true }),
        ]);
        setStats(statsRes.data);
        setAnalytics(analyticsRes.data);
      } catch {
        setStats(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return (
    <Layout>
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-teal-400 border-t-transparent rounded-full animate-spin" />
      </div>
    </Layout>
  );

  const aml = analytics?.aml_metrics || {};

  return (
    <Layout>
      <div data-testid="dashboard-page">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white" data-testid="dashboard-heading">
            Panel de Cumplimiento
          </h1>
          <p className="text-slate-400 mt-1 text-sm">
            {analytics?.business_line_label || "General"} &middot; UDI: ${analytics?.udi_value || "8.25"} MXN
          </p>
        </div>

        {/* Key Metrics Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="stats-grid">
          <MetricCard label="Clientes" value={stats?.total_customers || 0} icon={Users} color="teal" />
          <MetricCard label="Operaciones" value={stats?.total_operations || 0} icon={ArrowLeftRight} color="sky" />
          <MetricCard label="Alto Riesgo" value={stats?.high_risk_customers || 0} icon={Shield} color="red" />
          <MetricCard label="Alertas" value={stats?.total_alerts || 0} icon={AlertTriangle} color="amber" />
        </div>

        {/* AML Metrics Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="aml-metrics-grid">
          <MetricCard label="Act. Vulnerables" value={aml.aml_sensitive_count || 0} icon={Eye} color="violet"
            subtitle="Operaciones AML-sensibles" />
          <MetricCard label="Exceden Umbral" value={aml.threshold_exceeded_count || 0} icon={TrendingUp} color="amber"
            subtitle="Requieren aviso" />
          <MetricCard label="Screenings" value={aml.total_screenings || 0} icon={Search} color="teal"
            subtitle={`${aml.screening_matches || 0} coincidencias`} />
          <MetricCard label="Avisos Generados" value={aml.total_reports_generated || 0} icon={FileText} color="emerald"
            subtitle="Reportes AML" />
        </div>

        {/* Charts Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          {/* Risk Distribution Pie */}
          <ChartCard title="Distribución de Riesgo" testId="risk-distribution-chart">
            {analytics?.risk_distribution?.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={analytics.risk_distribution} dataKey="value" nameKey="name"
                    cx="50%" cy="50%" outerRadius={85} innerRadius={45} paddingAngle={3} strokeWidth={0}>
                    {analytics.risk_distribution.map((entry, i) => (
                      <Cell key={i} fill={RISK_COLORS[entry.name] || CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend iconType="circle" iconSize={8}
                    formatter={(v) => <span className="text-xs text-slate-300">{v}</span>} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-60 flex items-center justify-center text-slate-500 text-sm">Sin datos</div>
            )}
          </ChartCard>

          {/* Payment Distribution Pie */}
          <ChartCard title="Método de Pago" testId="payment-distribution-chart">
            {analytics?.payment_distribution?.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={analytics.payment_distribution} dataKey="value" nameKey="name"
                    cx="50%" cy="50%" outerRadius={85} innerRadius={45} paddingAngle={3} strokeWidth={0}>
                    {analytics.payment_distribution.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend iconType="circle" iconSize={8}
                    formatter={(v) => <span className="text-xs text-slate-300">{v}</span>} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-60 flex items-center justify-center text-slate-500 text-sm">Sin datos</div>
            )}
          </ChartCard>
        </div>

        {/* Charts Row 2 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          {/* Monthly Trend Line */}
          <ChartCard title="Tendencia Mensual de Operaciones" testId="monthly-trend-chart">
            {analytics?.monthly_trend?.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={analytics.monthly_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="month" tick={{ fill: "#94a3b8", fontSize: 11 }} />
                  <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend iconType="circle" iconSize={8}
                    formatter={(v) => <span className="text-xs text-slate-300">{v}</span>} />
                  <Line type="monotone" dataKey="count" name="Total" stroke="#0d9488" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="aml_sensitive" name="AML Sensible" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="threshold_exceeded" name="Excede Umbral" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-60 flex items-center justify-center text-slate-500 text-sm">Sin datos de tendencia</div>
            )}
          </ChartCard>

          {/* Operations by Type Bar */}
          <ChartCard title="Operaciones por Tipo" testId="ops-by-type-chart">
            {analytics?.ops_by_type?.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={analytics.ops_by_type} layout="vertical" margin={{ left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis type="number" tick={{ fill: "#94a3b8", fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" tick={{ fill: "#94a3b8", fontSize: 10 }} width={120} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="Operaciones" fill="#0d9488" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-60 flex items-center justify-center text-slate-500 text-sm">Sin datos</div>
            )}
          </ChartCard>
        </div>

        {/* Alerts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          {/* Alerts by Severity */}
          <ChartCard title="Alertas por Severidad" testId="alerts-severity-chart">
            {analytics?.alerts_by_severity?.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={analytics.alerts_by_severity}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 11 }} />
                  <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" name="Alertas" radius={[4, 4, 0, 0]}>
                    {(analytics.alerts_by_severity || []).map((entry, i) => (
                      <Cell key={i} fill={RISK_COLORS[entry.name] || CHART_COLORS[i]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-48 flex items-center justify-center text-slate-500 text-sm">Sin alertas abiertas</div>
            )}
          </ChartCard>

          {/* Alerts by Type */}
          <ChartCard title="Alertas por Tipo" testId="alerts-type-chart">
            {analytics?.alerts_by_type?.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={analytics.alerts_by_type} layout="vertical" margin={{ left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis type="number" tick={{ fill: "#94a3b8", fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" tick={{ fill: "#94a3b8", fontSize: 10 }} width={140} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" name="Alertas" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-48 flex items-center justify-center text-slate-500 text-sm">Sin alertas</div>
            )}
          </ChartCard>
        </div>

        {/* Volume Summary */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 mb-4" data-testid="volume-summary">
          <h3 className="text-sm font-semibold text-white mb-3">Resumen de Volumen</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-slate-500">Volumen Total</p>
              <p className="text-lg font-bold text-white">${(aml.total_volume_mxn || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })} MXN</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Volumen en Efectivo</p>
              <p className="text-lg font-bold text-amber-400">${(aml.cash_volume_mxn || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })} MXN</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">KYC Verificados</p>
              <p className="text-lg font-bold text-emerald-400">{stats?.verified_kyc || 0} / {stats?.total_customers || 0}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Revision Pendiente</p>
              <p className="text-lg font-bold text-orange-400">{stats?.operations_pending_review || 0}</p>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl" data-testid="recent-activity">
          <div className="px-5 py-4 border-b border-slate-800">
            <h2 className="text-sm font-semibold text-white">Actividad Reciente</h2>
          </div>
          <div className="divide-y divide-slate-800">
            {stats?.recent_activity?.length > 0 ? (
              stats.recent_activity.map((item, i) => (
                <div key={i} className="px-5 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-teal-400" />
                    <div>
                      <p className="text-sm text-slate-200">
                        <span className="font-medium">{item.user_name || "Sistema"}</span>
                        {" — "}
                        <span className="text-slate-400">{item.action}</span>
                      </p>
                      <p className="text-xs text-slate-500">{item.resource_type} / {item.resource_id}</p>
                    </div>
                  </div>
                  <span className="text-xs text-slate-500">
                    {item.created_at ? new Date(item.created_at).toLocaleString("es-MX") : ""}
                  </span>
                </div>
              ))
            ) : (
              <div className="px-5 py-8 text-center text-slate-500 text-sm">
                No hay actividad reciente
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
